import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { db } from "../../firebase";
import {
  doc,
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  getDocs,
  arrayUnion,
  runTransaction,
  serverTimestamp,
  arrayRemove,
} from "firebase/firestore";
import { toast } from "react-hot-toast";
import { Clock, Share2, AlertTriangle, Users, X } from "lucide-react";
import { countryToDial, toE164, digitsOnly } from "../../utils/phone";

import ShareCardModal from "./ShareCardModal";
import CreateCardModal from "./CreateCardModal";
import PayAgentFeesModal from "./PayAgentFeesModal";
import { releaseExpiredHoldsForAgent } from "./expireHolds";
import { cleanupAgentQueues, getFirstPendingCardId } from "./queueCleanup";
import AgentQueuesModal from "../../components/AgentQueuesModals";
import AgentAvailability from "../../components/AgentAvailability";

/* ───────── helpers ───────── */

async function findUserByPhone(dbRef, { e164, digits }) {
  const tries = [
    ["phoneE164", e164],
    ["phone", digits],
    ["mobile", digits],
    ["mobile", e164],
  ].filter(([, v]) => !!v);

  for (const [field, val] of tries) {
    const qy = query(collection(dbRef, "users"), where(field, "==", val));
    const snap = await getDocs(qy);
    if (!snap.empty) {
      const d = snap.docs[0];
      return { id: d.id, ...d.data() };
    }
  }
  return null;
}
function Stat({ label, value }) {
  return (
    <div className="p-4 bg-slate-800 rounded-xl ring-1 ring-slate-700">
      <div className="text-sm text-slate-400">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}
function symbolOf(code) {
  switch (code) {
    case "USD":
      return "$";
    case "EUR":
      return "€";
    case "GBP":
      return "£";
    case "INR":
      return "₹";
    case "LKR":
      return "Rs";
    default:
      return code || "$";
  }
}

/* ───────── Referrals modal ───────── */
function ReferralsModal({ open, onClose, agentUid }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open || !agentUid) return;
    setLoading(true);
    const qy = query(
      collection(db, "users"),
      where("referredByAgent", "==", agentUid),
      orderBy("createdAt", "desc")
    );
    const un = onSnapshot(
      qy,
      async (snap) => {
        const list = [];
        for (const d of snap.docs) {
          const u = { id: d.id, ...d.data() };
          // optional ticket count for this agent
          let ticketCount = 0;
          try {
            const tq = query(
              collection(db, "tickets"),
              where("userUid", "==", u.id),
              where("agentUid", "==", agentUid)
            );
            const tSnap = await getDocs(tq);
            ticketCount = tSnap.size;
          } catch {}
          list.push({ ...u, _ticketCount: ticketCount });
        }
        setRows(list);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => un();
  }, [open, agentUid]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full md:max-w-2xl bg-slate-900 border border-slate-700 rounded-2xl p-4 m-2">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Users className="text-lime-300" size={18} />
            <h3 className="font-semibold">Referred Users</h3>
          </div>
          <button
            onClick={onClose}
            className="px-2 py-1 rounded bg-slate-700 text-sm"
          >
            Close
          </button>
        </div>
        <div className="text-xs text-slate-400 mb-3">
          Users who opened your share link and bought or registered with you as
          referrer.
        </div>

        {loading ? (
          <div className="p-6 text-slate-300">Loading…</div>
        ) : rows.length ? (
          <div className="max-h-[60vh] overflow-auto space-y-2">
            {rows.map((u) => (
              <div
                key={u.id}
                className="p-3 rounded-xl bg-slate-800 ring-1 ring-slate-700 flex items-center gap-3"
              >
                <img
                  src={u.avatar || "/user.png"}
                  alt=""
                  className="w-10 h-10 rounded-full object-cover ring-1 ring-slate-600"
                />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">
                    {u.username || u.email || u.id.slice(0, 6)}
                  </div>
                  <div className="text-xs text-slate-300 truncate">
                    {u.email || "—"}
                  </div>
                  <div className="text-[11px] text-slate-400">
                    Mobile: {u.mobile || u.phone || "—"}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate-400">Tickets</div>
                  <div className="font-bold">{u._ticketCount}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-6 text-slate-300">No referrals yet.</div>
        )}
      </div>
    </div>
  );
}

/* ───────── Preview grid ───────── */
function CardPreviewGrid({ card }) {
  const filled = new Set(card.filled || []);
  const locked = new Set(card.locked || []);
  const all = [...Array(100).keys()];
  const sold = filled.size;
  const onHold = [...locked].filter((n) => !filled.has(n)).length;
  const available = 100 - new Set([...filled, ...locked]).size;
  const cellClass = (n) =>
    filled.has(n)
      ? "bg-lime-400 text-black font-bold"
      : locked.has(n)
      ? "bg-amber-400 text-black"
      : "bg-slate-700 text-white/90";
  return (
    <div className="w-full">
      <div className="flex flex-wrap gap-3 text-xs mb-3">
        <span className="inline-flex items-center gap-2">
          <span className="inline-block w-3.5 h-3.5 rounded bg-lime-400" />{" "}
          Sold: <b>{sold}</b>
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="inline-block w-3.5 h-3.5 rounded bg-amber-400" /> On
          Hold: <b>{onHold}</b>
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="inline-block w-3.5 h-3.5 rounded bg-slate-700" />{" "}
          Available: <b>{available}</b>
        </span>
      </div>
      <div className="grid grid-cols-10 gap-1">
        {all.map((n) => (
          <div
            key={n}
            className={`h-6 rounded text-[11px] leading-6 text-center select-none ${cellClass(
              n
            )}`}
          >
            {n.toString().padStart(2, "0")}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ───────── small UI helpers ───────── */
function ActionTile({ to, onClick, icon, title, subtitle }) {
  const Tag = to ? "a" : "button";
  const props = to ? { href: to } : { onClick, type: "button" };
  return (
    <Tag
      {...props}
      className="group flex items-center gap-3 rounded-2xl bg-slate-800 ring-1 ring-slate-700 hover:ring-lime-400/50 hover:bg-slate-700/80 transition px-4 py-3"
    >
      <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-slate-900/60 ring-1 ring-slate-700 text-lime-300">
        {icon}
      </span>
      <span className="text-left">
        <div className="font-semibold leading-5">{title}</div>
        {subtitle ? (
          <div className="text-xs text-slate-400 group-hover:text-slate-300">
            {subtitle}
          </div>
        ) : null}
      </span>
    </Tag>
  );
}
function CountPill({ color = "lime", children }) {
  const map = {
    lime: "bg-lime-400 text-black",
    blue: "bg-blue-400 text-black",
    amber: "bg-amber-400 text-black",
    slate: "bg-slate-600 text-white",
  };
  return (
    <span
      className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${map[color]}`}
    >
      {children}
    </span>
  );
}

function hasAnyRequestedFree(card, requestedSet) {
  const filled = new Set(card.filled || []);
  const locked = new Set(card.locked || []);
  for (const n of requestedSet) {
    if (!filled.has(n) && !locked.has(n)) return true;
  }
  return false;
}

/**
 * Locks VIP numbers on newly created cards.
 * - Skips numbers already filled/locked
 * - Writes 'locked' and saves a simple audit trail in /cards/{id}/vipReservations/{doc}
 */
/**
 * Apply VIP allocations to cards.
 * mode: "sell" (create ticket + move to filled) or "lock" (just hold)
 */

// async function applyVipPreallocToNewCards({
//   agentUid,
//   cardIds = [],
//   vips = [],
//   mode = "sell",
//   defaultDial = "+94",
// }) {
//   const perCard = new Map();
//   const ids = [...cardIds];

//   // distribute VIP rows across the chosen cards (still the same approach)
//   for (const vip of vips) {
//     const nums = Array.isArray(vip.nums) ? vip.nums : parseLucky(vip.lucky);
//     if (!nums.length) continue;
//     const howManyCards = Math.max(1, Number(vip.applyCards || 1));
//     for (let i = 0; i < Math.min(howManyCards, ids.length); i++) {
//       const cid = ids[i];
//       const arr = perCard.get(cid) || [];
//       arr.push({ mobile: (vip.mobile || "").trim(), nums });
//       perCard.set(cid, arr);
//     }
//   }

//   let ticketsCreated = 0;
//   let cardsTouched = 0;

//   // apply per card inside transactions
//   for (const [cid, reservations] of perCard.entries()) {
//     await runTransaction(db, async (tx) => {
//       const cardRef = doc(db, "cards", cid);
//       const snap = await tx.get(cardRef);
//       if (!snap.exists()) return;
//       const c = snap.data();

//       const filled = new Set(c.filled || []);
//       const locked = new Set(c.locked || []);

//       const safeAgentName =
//         c.agentName || "Agent " + (agentUid ? agentUid.slice(0, 6) : "");

//       let allocatedOnThisCard = 0;

//       for (const r of reservations) {
//         const lucky = r.nums || [];
//         const availableLucky = lucky.filter(
//           (n) => !filled.has(n) && !locked.has(n)
//         );
//         if (!availableLucky.length) continue;

//         if (mode === "lock") {
//           tx.update(cardRef, { locked: arrayUnion(...availableLucky) });
//           allocatedOnThisCard += availableLucky.length;
//           availableLucky.forEach((n) => locked.add(n));
//           continue;
//         }

//         // SELL path
//         const e164 = toE164(r.mobile, defaultDial);
//         const digits = digitsOnly(r.mobile);
//         let foundUser = null;
//         try {
//           foundUser = await findUserByPhone(db, { e164, digits });
//         } catch {}

//         const unit = Number(c.pricePerNumber || 0);
//         const amount = Number((availableLucky.length * unit).toFixed(2));
//         const tRef = doc(collection(db, "tickets"));
//         const bundleId = `${agentUid}|${foundUser?.id || e164}`;

//         tx.set(tRef, {
//           id: tRef.id,
//           status: "payment_approved",
//           allocated: true,
//           allocatedBy: agentUid,
//           allocatedAt: serverTimestamp(),
//           cardId: cid,

//           agentUid,
//           agentName: safeAgentName,

//           userUid: foundUser?.id || null,
//           userName:
//             foundUser?.username ||
//             foundUser?.email ||
//             (foundUser?.id
//               ? foundUser.id.slice(0, 6)
//               : `Mobile ${String(digits).slice(-4)}`),

//           userMobileE164: e164,
//           userMobile: e164,

//           numbers: availableLucky,
//           currency: c.currency || "USD",
//           unitPrice: unit,
//           amount,
//           paymentApprovedAt: serverTimestamp(),
//           bundleId,
//         });

//         tx.update(cardRef, {
//           filled: arrayUnion(...availableLucky),
//           locked: arrayRemove(...availableLucky),
//           todaySales: Number((c.todaySales || 0) + amount),
//         });

//         allocatedOnThisCard += availableLucky.length;
//         availableLucky.forEach((n) => filled.add(n));
//         ticketsCreated += 1; // one ticket per VIP row applied
//       }

//       if (allocatedOnThisCard > 0) cardsTouched += 1;
//     });
//   }

//   return { ticketsCreated, cardsTouched };
// }

async function applyVipPreallocToNewCards({
  agentUid,
  cardIds = [],
  vips = [],
  mode = "sell",
  defaultDial = "+94",
}) {
  if (!Array.isArray(cardIds) || !cardIds.length) return;

  // Normalize: ensure every VIP row carries applyCards; default to all passed cards
  const normalized = vips
    .map((v) => ({
      mobile: (v.mobile || "").trim(),
      nums: parseLucky(v.lucky),
      applyCards: Math.max(1, Number(v.applyCards || cardIds.length)),
    }))
    .filter((v) => v.nums.length > 0);

  if (!normalized.length) return;

  // Build cardId -> reservations[] (so each card gets all relevant VIP rows)
  const perCard = new Map();
  for (const vip of normalized) {
    const take = Math.min(vip.applyCards, cardIds.length);
    for (let i = 0; i < take; i++) {
      const cid = cardIds[i];
      const arr = perCard.get(cid) || [];
      arr.push({ mobile: vip.mobile, nums: vip.nums });
      perCard.set(cid, arr);
    }
  }

  // Now apply reservations per card in sequence (transaction per card)
  for (const [cid, reservations] of perCard.entries()) {
    console.info("[VIP] applying to card", cid, reservations);

    await runTransaction(db, async (tx) => {
      const cardRef = doc(db, "cards", cid);
      const snap = await tx.get(cardRef);
      if (!snap.exists()) {
        console.warn("[VIP] card not found:", cid);
        return;
      }
      const c = snap.data();

      const filled = new Set(c.filled || []);
      const locked = new Set(c.locked || []);
      const safeAgentName =
        c.agentName || "Agent " + (agentUid ? agentUid.slice(0, 6) : "");

      for (const r of reservations) {
        const lucky = r.nums || [];
        const availableLucky = lucky.filter(
          (n) => !filled.has(n) && !locked.has(n)
        );
        if (!availableLucky.length) continue;

        if (mode === "lock") {
          tx.update(cardRef, { locked: arrayUnion(...availableLucky) });
          // update local view so we don’t re-allocate within this tx
          for (const n of availableLucky) locked.add(n);
          continue;
        }

        // SELL mode: create paid ticket + move to filled
        const e164 = toE164(r.mobile, defaultDial);
        const digits = digitsOnly(r.mobile);

        // Look up user by phone (best-effort)
        let foundUser = null;
        try {
          foundUser = await findUserByPhone(db, { e164, digits });
        } catch {}

        const unit = Number(c.pricePerNumber || 0);
        const amount = Number((availableLucky.length * unit).toFixed(2));
        const tRef = doc(collection(db, "tickets"));
        const bundleId = `${agentUid}|${foundUser?.id || e164}`;

        tx.set(tRef, {
          id: tRef.id,
          status: "payment_approved",
          allocated: true,
          allocatedBy: agentUid,
          allocatedAt: serverTimestamp(),

          cardId: cid,
          agentUid,
          agentName: safeAgentName,

          userUid: foundUser?.id || null,
          userName:
            foundUser?.username ||
            foundUser?.email ||
            (foundUser?.id
              ? foundUser.id.slice(0, 6)
              : `Mobile ${String(digits).slice(-4)}`),

          userMobileE164: e164,
          userMobile: e164,

          numbers: availableLucky,
          currency: c.currency || "USD",
          unitPrice: unit,
          amount,
          paymentApprovedAt: serverTimestamp(),
          bundleId,
        });

        tx.update(cardRef, {
          filled: arrayUnion(...availableLucky),
          locked: arrayRemove(...availableLucky),
          todaySales: Number((c.todaySales || 0) + amount),
        });

        // Update local sets so later VIP rows within the same card see these as taken
        for (const n of availableLucky) filled.add(n);
      }
    });
  }
}

function parseLucky(str = "") {
  const set = new Set();
  for (const raw of String(str).split(/[^\d]+/g)) {
    if (raw === "") continue;
    const n = Number(raw);
    if (Number.isInteger(n) && n >= 0 && n <= 99) set.add(n);
  }
  return [...set];
}

/* ───────── main ───────── */
export default function AgentDashboard({ user }) {
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [pendingReqs, setPendingReqs] = useState(0);
  const [pendingPays, setPendingPays] = useState(0);

  const [shareOpen, setShareOpen] = useState(false);
  const [shareCardId, setShareCardId] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [payFeesOpen, setPayFeesOpen] = useState(false);

  const [agentDoc, setAgentDoc] = useState(null);

  const [refModal, setRefModal] = useState(false);
  const [refCount, setRefCount] = useState(0);

  const [open, setOpen] = useState(false);

  const [vips, setVips] = useState([{ mobile: "", lucky: "", applyCards: 1 }]);
  const [busy, setBusy] = useState(false);
  const howMany = 10;
  const [vipApplyCount, setVipApplyCount] = useState(1);

  // referrals counter
  useEffect(() => {
    if (!user?.uid) return;
    const qy = query(
      collection(db, "users"),
      where("referredByAgent", "==", user.uid)
    );
    const un = onSnapshot(qy, (s) => setRefCount(s.size));
    return () => un();
  }, [user?.uid]);

  // housekeeping on mount
  useEffect(() => {
    if (!user?.uid) return;
    cleanupAgentQueues(user.uid);
    const id = setInterval(() => cleanupAgentQueues(user.uid), 30000);
    return () => clearInterval(id);
  }, [user?.uid]);

  // agent doc
  useEffect(() => {
    if (!user?.uid) return;
    const unsub = onSnapshot(doc(db, "agents", user.uid), (d) =>
      setAgentDoc(d.exists() ? { id: d.id, ...d.data() } : null)
    );
    return () => unsub?.();
  }, [user?.uid]);

  // my cards
  useEffect(() => {
    if (!user?.uid) return;
    const qy = query(
      collection(db, "cards"),
      where("agentUid", "==", user.uid),
      orderBy("createdAt", "desc")
    );
    return onSnapshot(
      qy,
      (snap) => {
        const arr = [];
        snap.forEach((d) => arr.push({ id: d.id, ...d.data() }));
        setCards(arr);
      },
      (err) => toast.error(`Cards stream error: ${err.message}`)
    );
  }, [user?.uid]);

  // queues
  useEffect(() => {
    if (!user?.uid) return;
    const un1 = onSnapshot(
      query(
        collection(db, "tickets"),
        where("agentUid", "==", user.uid),
        where("status", "==", "requested")
      ),
      (s) => setPendingReqs(s.size)
    );
    const un2 = onSnapshot(
      query(
        collection(db, "tickets"),
        where("agentUid", "==", user.uid),
        where("status", "==", "payment_submitted")
      ),
      (s) => setPendingPays(s.size)
    );
    return () => {
      un1();
      un2();
    };
  }, [user?.uid]);

  // release expired holds
  useEffect(() => {
    if (!user?.uid) return;
    releaseExpiredHoldsForAgent(user.uid);
    const id = setInterval(() => releaseExpiredHoldsForAgent(user.uid), 30000);
    return () => clearInterval(id);
  }, [user?.uid]);

  const handleOpenQueues = async () => {
    try {
      await cleanupAgentQueues(user.uid);
      const firstCardId = await getFirstPendingCardId(user.uid);
      if (firstCardId) {
        navigate(`/agent/cards/${encodeURIComponent(firstCardId)}`);
      } else {
        toast("No pending queues");
      }
    } catch (e) {
      console.error(e);
      toast.error("Couldn’t open queues");
    }
  };

  const activeCards = useMemo(
    () => cards.filter((c) => c.status === "active"),
    [cards]
  );

  const outstanding = Number(agentDoc?.feeOutstandingUSD || 0);
  const pending = Number(agentDoc?.feePendingUSD || 0);
  const perCard = Number(agentDoc?.cardFeePerCardUSD ?? 1);
  const status = agentDoc?.status || "pending"; // approved | pending | rejected

  async function handleApplyVipNow() {
    if (busy) return; // tiny guard
    try {
      setBusy(true);

      // 1) how many newest active cards to apply
      const N = Math.max(1, Math.min(vipApplyCount, activeCards.length));
      const targetCards = activeCards.slice(0, N).map((c) => c.id);
      if (!targetCards.length) {
        toast.error("No active cards to apply.");
        return;
      }

      // 2) validate VIP rows
      const validVips = vips.filter((v) => parseLucky(v.lucky).length > 0);
      if (!validVips.length) {
        toast.error("Enter at least one VIP with valid lucky numbers (0–99).");
        return;
      }

      // 3) tell the helper to apply each VIP to N cards
      const vipsForApply = validVips.map((v) => ({ ...v, applyCards: N }));

      const defaultDial =
        countryToDial(agentDoc?.countryCode || agentDoc?.country) || "+94";

      // 4) apply across target cards
      await applyVipPreallocToNewCards({
        agentUid: user?.uid,
        cardIds: targetCards,
        vips: vipsForApply,
        mode: "sell",
        defaultDial:
          countryToDial(agentDoc?.countryCode || agentDoc?.country) || "+94",
      });

      // 5) reset UI
      setVips([{ mobile: "", lucky: "", applyCards: 1 }]);
      setVipApplyCount(1);
      toast.success(`VIPs applied to ${N} card${N > 1 ? "s" : ""}.`);
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Couldn’t apply VIP allocation");
    } finally {
      setBusy(false);
    }
  }

  function addVipRow() {
    setVips((arr) => [...arr, { mobile: "", lucky: "", applyCards: 1 }]);
  }
  function removeVipRow(i) {
    setVips((arr) =>
      arr.length > 1 ? arr.filter((_, idx) => idx !== i) : arr
    );
  }
  function updateVip(i, patch) {
    setVips((arr) =>
      arr.map((row, idx) => (idx === i ? { ...row, ...patch } : row))
    );
  }

  return (
    <div className="max-w-6xl mx-auto mt-8 md:mt-16">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Agent Dashboard</h1>
        <div className="flex gap-2">
          <Link
            to="/lottery"
            className="px-3 py-2 rounded-xl bg-slate-800 ring-1 ring-slate-700 text-white hover:bg-slate-700"
          >
            Open Player View
          </Link>
          <button
            onClick={() =>
              status === "approved"
                ? setCreateOpen(true)
                : toast.error("Your agent status is not approved")
            }
            className={`px-3 py-2 rounded-xl font-semibold ${
              status === "approved"
                ? "bg-lime-400 text-black hover:brightness-95"
                : "bg-slate-600 text-slate-300 cursor-not-allowed"
            }`}
            title={
              status === "approved" ? "Create Card" : "Agent is not approved"
            }
          >
            + Create Card
          </button>
        </div>
      </div>

      {/* Fees + referrals */}
      <div className="mt-4 p-4 rounded-2xl bg-slate-800 ring-1 ring-slate-700 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <AlertTriangle className="text-yellow-300" size={22} />
          <div className="text-sm">
            <div>
              Per-card fee: <b>${perCard.toFixed(2)}</b>
            </div>
            <div>
              Outstanding: <b>${outstanding.toFixed(2)}</b>{" "}
              {pending > 0 && (
                <span className="text-slate-300">
                  · Pending: <b>${pending.toFixed(2)}</b>
                </span>
              )}
            </div>
            {status !== "approved" && (
              <div className="text-red-300">
                Status: <b className="capitalize">{status}</b> (you cannot
                create cards)
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="px-3 py-2 rounded-xl bg-slate-900/60 ring-1 ring-slate-700 text-sm flex items-center gap-2">
            <Users size={16} className="text-lime-300" />
            <span className="font-semibold">{refCount}</span>
            <span className="text-slate-300">referrals</span>
            <button
              onClick={() => setRefModal(true)}
              className="ml-2 px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 text-xs"
            >
              View
            </button>
          </div>

          <button
            onClick={() => setPayFeesOpen(true)}
            className="px-3 py-2 rounded-xl bg-indigo-400 text-black font-semibold hover:brightness-95"
          >
            Pay Fees
          </button>
        </div>
      </div>

      {/* Quick actions + Queues */}
      <div className="mt-8 grid md:grid-cols-3 gap-4">
        {/* Left: action tiles */}
        <div className="md:col-span-2 space-y-3">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <ActionTile
              to="/agent/banks"
              icon={<span className="text-lg">🏦</span>}
              title="Payment Methods"
              subtitle="Add bank accounts & Binance Pay"
            />
            <ActionTile
              to="/agent/history"
              icon={<span className="text-lg">🗂️</span>}
              title="Card History"
              subtitle="Past cards & winners"
            />
            <ActionTile
              onClick={() => setShareOpen(true)}
              icon={<span className="text-lg">🔗</span>}
              title="Share Link"
              subtitle="Copy your public lottery page"
            />
          </div>
        </div>

        {/* Right: queues card */}
        <div className="p-4 bg-slate-800 rounded-2xl ring-1 ring-slate-700 h-max">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">Queues</h2>
            <button
              onClick={() => setOpen(true)}
              className="px-3 py-1.5 rounded-xl bg-lime-400 text-black font-semibold hover:brightness-95"
            >
              Open queues
            </button>
          </div>
          <AgentQueuesModal
            open={open}
            onClose={() => setOpen(false)}
            user={user}
            // cardId={activeCardId} // pass the selected card id
          />

          <div className="mt-3 space-y-3">
            <div className="rounded-xl bg-slate-900/60 ring-1 ring-slate-700 px-3 py-2.5">
              <div className="flex items-center justify-between">
                <div className="text-sm">Requests waiting approval</div>
                <CountPill color="amber">{pendingReqs}</CountPill>
              </div>
              <div className="text-[11px] text-slate-400 mt-1">
                Players picked numbers and are waiting for your payment details.
              </div>
            </div>

            <div className="rounded-xl bg-slate-900/60 ring-1 ring-slate-700 px-3 py-2.5">
              <div className="flex items-center justify-between">
                <div className="text-sm">Payments to verify</div>
                <CountPill color="blue">{pendingPays}</CountPill>
              </div>
              <div className="text-[11px] text-slate-400 mt-1">
                Players uploaded slips. Approve to confirm their numbers.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <Stat label="Active Cards" value={activeCards.length} />
        <Stat label="Pending Requests" value={pendingReqs} />
        <Stat label="Pending Payments" value={pendingPays} />
        <Stat
          label="Today’s Sales"
          value={`${activeCards.reduce((s, c) => s + (c.todaySales || 0), 0)}`}
        />
      </div>

      <AgentAvailability
        agentUid={user?.uid}
        cards={cards}
        statuses={["active"]}
      />

      <div className="mt-4 p-4 rounded-2xl bg-slate-800 border border-slate-700">
        <div className="flex items-center justify-between mb-2">
          <div className="font-semibold">VIP Pre-Allocation</div>
          <button
            type="button"
            onClick={addVipRow}
            className="px-2 py-1 rounded bg-slate-700 text-sm"
            disabled={busy}
          >
            + Add VIP
          </button>
        </div>
        <div className="text-xs text-slate-400 mb-3">
          For each VIP: enter <b>mobile</b> and <b>lucky numbers</b>{" "}
          (comma-separated). Each VIP’s lucky numbers will be allocated{" "}
          <b>once per card</b> on the first <b>“Cards to apply”</b> newly
          created cards (max equals the number of cards you’re creating).
        </div>

        <div className="space-y-2">
          {vips.map((r, i) => (
            <div
              key={i}
              className="grid sm:grid-cols-[1fr_1fr_140px_auto] gap-2 items-center"
            >
              <input
                placeholder="Lucky numbers (e.g. 24, 36, 89)"
                value={r.lucky}
                onChange={(e) => updateVip(i, { lucky: e.target.value })}
                className="px-3 py-2 rounded bg-slate-900 text-white ring-1 ring-slate-700"
                disabled={busy}
              />
              <input
                placeholder="Mobile (e.g. +94761234567 or 0761234567)"
                value={r.mobile}
                onChange={(e) => updateVip(i, { mobile: e.target.value })}
                className="px-3 py-2 rounded bg-slate-900 text-white ring-1 ring-slate-700"
                disabled={busy}
              />
              <button
                onClick={() => removeVipRow(i)}
                className="px-3 py-2 rounded bg-slate-700 text-white"
                disabled={busy || vips.length === 1}
                title="Remove row"
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <label className="text-xs text-slate-300">Apply to latest</label>
          <input
            type="number"
            min={1}
            max={Math.max(1, activeCards.length)}
            value={vipApplyCount}
            onChange={(e) =>
              setVipApplyCount(
                Math.max(
                  1,
                  Math.min(Number(e.target.value || 1), activeCards.length || 1)
                )
              )
            }
            className="w-20 px-3 py-2 rounded bg-slate-900 text-white ring-1 ring-slate-700 text-sm"
            title="How many of your newest active cards to apply this VIP allocation to"
            disabled={busy}
          />
          <span className="text-xs text-slate-300">card(s)</span>
          <button
            type="button"
            onClick={handleApplyVipNow}
            disabled={busy || activeCards.length === 0}
            className="ml-2 px-3 py-2 rounded-xl bg-lime-400 text-black font-semibold hover:brightness-95 disabled:opacity-60 text-sm"
          >
            Apply VIP Now
          </button>
          <button
            type="button"
            onClick={() => setVips([{ mobile: "", lucky: "", applyCards: 1 }])}
            disabled={busy}
            className="px-3 py-2 rounded-xl bg-slate-700 text-white hover:bg-slate-600 text-sm"
            title="Clear all VIP rows"
          >
            Clear
          </button>
          <div className="text-xs text-slate-500 ml-auto">
            Newest first · {activeCards.length} active card
            {activeCards.length === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      {/* Open cards */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">
            Open Cards (with selections & sales)
          </h2>
          <div className="text-xs text-slate-400">
            Green = Sold · Amber = On Hold
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {activeCards.map((c) => (
            <div
              key={c.id}
              className="p-4 bg-slate-800 rounded-2xl ring-1 ring-slate-700"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="px-2 py-0.5 rounded bg-yellow-300 text-black text-sm font-bold">
                    {c.filled?.length || 0}/100
                  </div>
                  <div>
                    <div className="font-semibold">
                      Card #{c.id.slice(0, 8)}…
                    </div>
                    <div className="text-xs text-slate-300 flex items-center gap-2">
                      <Clock size={14} />
                      <span>
                        Draw{" "}
                        {c.drawAt
                          ? new Date(
                              c.drawAt?.toDate?.() || c.drawAt
                            ).toLocaleTimeString()
                          : "-"}
                      </span>
                      <span className="mx-1">•</span>
                      <span>
                        {symbolOf(c.currency)}
                        {Number(c.pricePerNumber || 0).toFixed(2)}/num
                      </span>
                      <span className="mx-1">•</span>
                      <span>Payout {c.payoutPct ?? 80}%</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Link
                    to={`/agent/cards/${encodeURIComponent(c.id)}`}
                    className="px-2 py-1 rounded bg-slate-700 text-sm"
                  >
                    Manage
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      setShareCardId(c.id);
                      setShareOpen(true);
                    }}
                    className="px-2 py-1 rounded bg-slate-600 hover:bg-slate-500 text-sm flex items-center gap-1"
                  >
                    <Share2 size={16} /> Share
                  </button>
                </div>
              </div>
              <CardPreviewGrid card={c} />
            </div>
          ))}
        </div>

        {!activeCards.length && (
          <div className="text-slate-400 text-sm">No active cards.</div>
        )}
      </div>

      {/* Modals */}
      <ShareCardModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        agentUid={user?.uid}
        currentCardId={shareCardId}
      />
      <CreateCardModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        agentUid={user?.uid}
        onCreated={async (ids) => {
          if (!ids?.length) return;
          try {
            setBusy(true);
            const vipsForApply = vips
              .filter((v) => parseLucky(v.lucky).length > 0)
              .map((v) => ({ ...v, applyCards: ids.length })); // all new cards

            await applyVipPreallocToNewCards({
              agentUid: user?.uid,
              cardIds: ids,
              vips: vipsForApply,
              mode: "sell",
              defaultDial:
                countryToDial(agentDoc?.countryCode || agentDoc?.country) ||
                "+94",
            });
          } finally {
            setBusy(false);
          }
          navigate(`/agent/cards/${ids[0]}`);
        }}
        agentStatus={status}
      />
      <PayAgentFeesModal
        open={payFeesOpen}
        onClose={() => setPayFeesOpen(false)}
        agent={agentDoc}
      />
      <ReferralsModal
        open={refModal}
        onClose={() => setRefModal(false)}
        agentUid={user?.uid}
      />
    </div>
  );
}
